import {
  type InfiniteData,
  type Query,
  type QueryClient,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import type { DocumentSummary } from "@/types/document";
import type {
  Fax,
  FaxAnalysis,
  FaxListFilters,
  FaxListItem,
  FaxPatientLinkResult,
  FaxSuggestionKind,
  LinkFaxPatientRequest,
  SendFaxRequest,
} from "@/types/fax";
import type { CursorPage, Page } from "@/types/pagination";

import {
  analyzeFax,
  createNoteFromFax,
  dismissFaxSuggestion,
  fetchFaxUnreadCount,
  fetchFaxes,
  getFax,
  linkFaxToPatient,
  markFaxRead,
  sendFax,
  unlinkFaxPatient,
} from "./faxes";
import { noteQueryKeys } from "@/api/provider/scribe/note-query";
import { documentQueryKeys } from "@/api/provider/user/patient/document-query";
import { optimisticAdjustSidebarCounts } from "@/api/provider/user/patient/sidebar-cache-utils";
import { notificationQueryKeys } from "@/query/notifications/notifications";

// Sibling tree of faxContactsQueryKeys (rooted at ["fax-contacts"]) — these
// trees must stay disjoint so a fax send/list invalidation doesn't cascade
// into every cached contacts query.
export const faxesQueryKeys = {
  all: ["faxes"] as const,
  list: (filters: Omit<FaxListFilters, "cursor"> = {}) =>
    [...faxesQueryKeys.all, "list", filters] as const,
  detail: (id: string) => [...faxesQueryKeys.all, "detail", id] as const,
  unreadCount: () => [...faxesQueryKeys.all, "unread-count"] as const,
  costEstimate: (gcsPaths: string[], coverSheetEnabled = false) =>
    [
      ...faxesQueryKeys.all,
      "cost-estimate",
      gcsPaths,
      coverSheetEnabled,
    ] as const,
};

export const faxesQueryOptions = {
  unreadCount: () => ({
    queryKey: faxesQueryKeys.unreadCount(),
    queryFn: () => fetchFaxUnreadCount(),
    staleTime: 30 * 1000,
  }),
};

const faxesListKey = [...faxesQueryKeys.all, "list"] as const;

function faxToListItem(fax: Fax): FaxListItem {
  return {
    id: fax.id,
    direction: fax.direction,
    status: fax.status,
    faxContact: fax.faxContact,
    phoneNumberId: fax.phoneNumberId,
    phoneNumberE164: fax.phoneNumberE164,
    numPages: fax.numPages,
    sentAt: fax.sentAt,
    completedAt: fax.completedAt,
    createdDate: fax.createdDate,
    readDate: fax.readDate,
  };
}

/**
 * Patch a patient's cached chart document list in place from a server-returned
 * document — the response-driven alternative to invalidating + refetching. The
 * cache holds a `Page<DocumentSummary>` (consumers read `.data` via `select`), so
 * we edit `data` and adjust `total` by the size delta. If the list was never
 * fetched (`undefined`), we leave it alone — the next mount fetches fresh, which
 * matches what an invalidation would have done for an unmounted query.
 */
function patchPatientDocList(
  queryClient: QueryClient,
  patientId: string,
  update: (docs: DocumentSummary[]) => DocumentSummary[],
): void {
  queryClient.setQueryData<Page<DocumentSummary> | null>(
    documentQueryKeys.list(patientId),
    (old) => {
      if (!old) return old;
      const data = update(old.data);
      return {
        ...old,
        data,
        total: old.total + (data.length - old.data.length),
      };
    },
  );
}

export function useFaxes(filters: Omit<FaxListFilters, "cursor"> = {}) {
  return useInfiniteQuery({
    queryKey: faxesQueryKeys.list(filters),
    queryFn: ({ pageParam }) =>
      fetchFaxes({ ...filters, cursor: pageParam as string | undefined }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last?.nextCursor ?? undefined,
  });
}

type FaxQuery = Query<Fax | null, Error, Fax | null, readonly unknown[]>;

interface UseFaxOptions {
  refetchInterval?:
    | number
    | false
    | ((query: FaxQuery) => number | false | undefined);
}

export function useFax(id: string | undefined, options: UseFaxOptions = {}) {
  return useQuery<Fax | null, Error, Fax | null, readonly unknown[]>({
    queryKey: id
      ? faxesQueryKeys.detail(id)
      : [...faxesQueryKeys.all, "detail", "disabled"],
    queryFn: () => (id ? getFax(id) : Promise.resolve(null)),
    enabled: Boolean(id),
    refetchInterval: options.refetchInterval,
  });
}

export function useSendFax() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: SendFaxRequest) => sendFax(data),
    onSuccess: (fax: Fax | null) => {
      if (!fax) return;
      queryClient.setQueryData(faxesQueryKeys.detail(fax.id), fax);
      // Prepend the new fax to every cached list page; no invalidation needed
      // since the response is authoritative and ordered by created_date desc.
      const listItem = faxToListItem(fax);
      queryClient.setQueriesData<InfiniteData<CursorPage<FaxListItem>>>(
        { queryKey: faxesListKey },
        (old) => {
          if (!old || old.pages.length === 0) return old;
          const [firstPage, ...rest] = old.pages;
          if (!firstPage) return old;
          return {
            ...old,
            pages: [
              {
                ...firstPage,
                data: [listItem, ...firstPage.data],
                total: firstPage.total + 1,
              },
              ...rest,
            ],
          };
        },
      );
    },
  });
}

/**
 * Flip the fax row's tenant-wide read state. The server sets
 * ``Fax.read_date`` (idempotent — first-writer-wins in SQL) and clears every
 * provider's ``FAX_RECEIVED`` notification for that fax, mirroring how chat
 * marks the conversation read for everyone at once.
 *
 * Optimistically patches ``readDate`` on every cached list row matching the
 * id so the sidebar's "New" badge clears immediately. The bell-icon
 * notification feed and the sidebar unread count are invalidated on success
 * since the server flipped notifications across the tenant.
 */
export function useMarkFaxRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (faxId: string) => markFaxRead(faxId),
    onMutate: async (faxId: string) => {
      const now = new Date().toISOString();
      const caches = queryClient.getQueriesData<
        InfiniteData<CursorPage<FaxListItem>>
      >({
        queryKey: [...faxesQueryKeys.all, "list"],
      });
      for (const [queryKey, data] of caches) {
        if (!data) continue;
        let hit = false;
        const nextPages = data.pages.map((page) => {
          if (!page) return page;
          let pageHit = false;
          const nextData = page.data.map((row) => {
            if (row.id !== faxId || row.readDate) return row;
            pageHit = true;
            return { ...row, readDate: now };
          });
          if (!pageHit) return page;
          hit = true;
          return { ...page, data: nextData };
        });
        if (!hit) continue;
        queryClient.setQueryData<InfiniteData<CursorPage<FaxListItem>>>(
          queryKey,
          { ...data, pages: nextPages },
        );
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: faxesQueryKeys.unreadCount() });
      queryClient.invalidateQueries({ queryKey: notificationQueryKeys.all });
    },
    // Self-heal only on failure: a successful mark-read already matches the
    // optimistic patch (``readDate`` is consumed purely as a boolean), so
    // refetching every list page on success is needless DB load. On error we
    // re-fetch to drop the fake ``readDate`` instead of waiting out staleTime.
    onError: () => {
      queryClient.invalidateQueries({
        queryKey: [...faxesQueryKeys.all, "list"],
      });
    },
  });
}

/**
 * File an inbound fax into a patient's chart. The server creates (or, on a
 * re-point to a different patient, moves in place) a Document that references
 * the fax's own PDF — the fax→patient link is derived from it — and
 * re-confirming the same patient is a no-op.
 *
 * Fully response-driven, no invalidation (mirrors `useSendFax` /
 * `useFaxContactMutations`): the endpoint returns the updated `fax` *and* the
 * `affectedDocument` it filed, so we write the fax into the detail cache and
 * splice the document straight into the new patient's chart list. On a re-point
 * we also drop it from the previous patient's chart list (`previousPatientId`).
 * The fax list is left untouched — no `FaxListItem` field reflects linkage. If
 * the response is `null`, the detail query is invalidated so the pane can't keep
 * showing stale linkage.
 */
export function useLinkFaxToPatient() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      faxId,
      data,
    }: {
      faxId: string;
      data: LinkFaxPatientRequest;
      previousPatientId?: string | null;
    }) => linkFaxToPatient(faxId, data),
    onSuccess: (
      result: FaxPatientLinkResult | null,
      { faxId, previousPatientId },
    ) => {
      if (!result) {
        queryClient.invalidateQueries({
          queryKey: faxesQueryKeys.detail(faxId),
        });
        return;
      }
      const { fax, affectedDocument } = result;
      queryClient.setQueryData(faxesQueryKeys.detail(fax.id), fax);
      if (!affectedDocument) return;
      // Add (or replace) the filed document at the top of the new patient's chart.
      const newPatientId = affectedDocument.patientId;
      const movedFromPrevious =
        !!previousPatientId && previousPatientId !== newPatientId;
      patchPatientDocList(queryClient, newPatientId, (docs) => [
        affectedDocument,
        ...docs.filter((d) => d.id !== affectedDocument.id),
      ]);
      // Re-point: the moved document leaves the previous patient's chart.
      if (movedFromPrevious) {
        patchPatientDocList(queryClient, previousPatientId, (docs) =>
          docs.filter((d) => d.id !== affectedDocument.id),
        );
      }
      // Keep the right-sidebar documents count (which gates the Documents
      // accordion's enabled state) in sync the way upload/delete do. Re-confirming
      // the same patient (previousPatientId === newPatientId) changes nothing; a
      // first link or re-point adds one to the new patient; a re-point also drops
      // one from the previous patient.
      if (previousPatientId !== newPatientId) {
        optimisticAdjustSidebarCounts(newPatientId, { documentsCount: 1 });
      }
      if (movedFromPrevious) {
        optimisticAdjustSidebarCounts(previousPatientId, {
          documentsCount: -1,
        });
      }
    },
  });
}

/**
 * Patch just the `analysis` slice of the cached fax detail from a
 * server-returned row — response-driven, no invalidation (read-replica rule).
 * If the detail was never fetched, leave it alone; the next mount fetches
 * fresh, matching what an invalidation would have done for an unmounted query.
 */
function patchFaxAnalysis(
  queryClient: QueryClient,
  faxId: string,
  analysis: FaxAnalysis,
): void {
  queryClient.setQueryData<Fax | null>(faxesQueryKeys.detail(faxId), (old) =>
    old ? { ...old, analysis } : old,
  );
}

/**
 * (Re)trigger the AI analysis for an inbound fax. The response is the reset
 * PENDING row; writing it into the detail cache flips the suggestions strip
 * back to its shimmer state. The finished run fills the strip in on the next
 * fax-detail refetch — the FAX_ANALYSIS_UPDATE realtime push that triggers that
 * refetch when analysis completes lands in a later change (see the
 * FaxAnalysisSuggestions header).
 */
export function useAnalyzeFax() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ faxId }: { faxId: string }) => analyzeFax(faxId),
    onSuccess: (analysis: FaxAnalysis | null, { faxId }) => {
      if (!analysis) {
        queryClient.invalidateQueries({
          queryKey: faxesQueryKeys.detail(faxId),
        });
        return;
      }
      patchFaxAnalysis(queryClient, faxId, analysis);
    },
  });
}

/**
 * Generate a clinical note from the fax (the "note" suggestion's accept).
 * The request is slow (~10-30s of LLM template fill) — callers key their
 * "Generating note…" state off `isPending`. The response carries the updated
 * analysis with `createdNoteId`, written into the detail cache so the card
 * flips to "Open note".
 *
 * A real Note now exists on the linked patient's chart, so the note-side caches
 * need broadly the treatment `useCreateNoteMutation` gives them: the scribe
 * list/counts are invalidated and the patient's sidebar notes count is bumped,
 * or the user comes back from the generated note and can't find it. The group
 * index is invalidated too — that one goes *beyond* the scribe path on purpose;
 * see the comment on that line. Only the
 * *note-side* work is mirrored — that mutation also invalidates patient detail,
 * Today's Items and prefilled answers, but all three exist to pick up
 * note-mapping writes (meds/measurements/sold items), and this endpoint
 * deliberately skips mappings ("an external document shouldn't silently mutate
 * chart data"), so those would be pure refetch churn. Revisions are skipped too:
 * the generated note is the root of its own chain, so no cached revision list
 * can contain it.
 */
export function useCreateNoteFromFax() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      faxId,
      noteTemplateId,
    }: {
      faxId: string;
      noteTemplateId?: string;
    }) => createNoteFromFax(faxId, noteTemplateId),
    onSuccess: (analysis: FaxAnalysis | null, { faxId }) => {
      if (!analysis) {
        queryClient.invalidateQueries({
          queryKey: faxesQueryKeys.detail(faxId),
        });
        return;
      }
      // Read the fax before patching: it carries both the pre-call note id and
      // the linked patient (the same patient the server generated onto), and
      // patchFaxAnalysis is about to overwrite the analysis slice.
      const cachedFax = queryClient.getQueryData<Fax | null>(
        faxesQueryKeys.detail(faxId),
      );
      // The endpoint is idempotent — a second call returns the *existing* note's
      // analysis unchanged. Only treat it as a creation when the id actually
      // changed, or a retry/double-submit would inflate the sidebar count.
      const createdNote =
        !!analysis.createdNoteId &&
        analysis.createdNoteId !== cachedFax?.analysis?.createdNoteId;

      patchFaxAnalysis(queryClient, faxId, analysis);
      if (!createdNote) return;

      queryClient.invalidateQueries({ queryKey: noteQueryKeys.lists() });
      queryClient.invalidateQueries({ queryKey: noteQueryKeys.counts() });
      // `groups()` is a *sibling* of `lists()`, not a child, so the line above
      // can't reach it. The scribe table drives off that group index to decide
      // which groups exist before it fetches any group's rows, and sums its
      // totals for the "no notes" empty state — both cached 2 minutes. Without
      // this, a note landing in a group the index doesn't list yet (first note
      // of the day, or a table that was empty) stays invisible until the index
      // goes stale on its own.
      //
      // Deliberately more than `useCreateNoteMutation` does: nothing in the app
      // invalidates `groups()`, so the scribe flow has this same gap. That is a
      // pre-existing app-wide bug tracked separately — this line is not an
      // oversight to "tidy up" into consistency with the scribe path.
      queryClient.invalidateQueries({ queryKey: noteQueryKeys.groups() });
      const patientId = cachedFax?.patient?.id;
      if (patientId) {
        optimisticAdjustSidebarCounts(patientId, { notesCount: 1 });
      }
    },
  });
}

/**
 * Hide one suggestion card (patient / note / task). Idempotent server-side;
 * the response carries the updated analysis, which is written straight into
 * the detail cache so the card disappears without a refetch.
 */
export function useDismissFaxSuggestion() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ faxId, kind }: { faxId: string; kind: FaxSuggestionKind }) =>
      dismissFaxSuggestion(faxId, kind),
    onSuccess: (analysis: FaxAnalysis | null, { faxId }) => {
      if (!analysis) {
        queryClient.invalidateQueries({
          queryKey: faxesQueryKeys.detail(faxId),
        });
        return;
      }
      patchFaxAnalysis(queryClient, faxId, analysis);
    },
  });
}

/**
 * Undo a fax→patient filing: the server archives the chart document this fax
 * produced, which dissolves the derived patient link. Fully response-driven (no
 * invalidation): the updated patient-less `fax` is written into the detail cache,
 * and the returned `affectedDocument` (the now-archived filing) is spliced out of
 * the previously-linked patient's chart list by id. The fax list is untouched. If
 * the response is `null`, the detail query is invalidated instead.
 */
export function useUnlinkFaxPatient() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ faxId }: { faxId: string }) => unlinkFaxPatient(faxId),
    onSuccess: (result: FaxPatientLinkResult | null, { faxId }) => {
      if (!result) {
        queryClient.invalidateQueries({
          queryKey: faxesQueryKeys.detail(faxId),
        });
        return;
      }
      const { fax, affectedDocument } = result;
      queryClient.setQueryData(faxesQueryKeys.detail(fax.id), fax);
      if (affectedDocument) {
        // Trust the archived filing's own patient_id over any caller-supplied id:
        // it's the patient the document was actually filed under, even if the fax
        // was re-linked elsewhere between this dialog opening and the unlink.
        const filedPatientId = affectedDocument.patientId;
        patchPatientDocList(queryClient, filedPatientId, (docs) =>
          docs.filter((d) => d.id !== affectedDocument.id),
        );
        optimisticAdjustSidebarCounts(filedPatientId, { documentsCount: -1 });
      }
    },
  });
}
